'use client';

import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

interface ChartDataPoint {
    month: string;
    income: number;
    expense: number;
}

interface CompactFinancialChartProps {
    data: ChartDataPoint[];
    title?: string;
    subtitle?: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-surface border border-border-main p-3 rounded-xl shadow-xl">
                <p className="font-bold text-foreground mb-1.5 text-xs">{label}</p>
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span className="text-[10px] text-secondary font-medium">Masuk:</span>
                        <span className="text-[10px] font-bold text-foreground ml-auto">
                            {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(payload[0].value)}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                        <span className="text-[10px] text-secondary font-medium">Keluar:</span>
                        <span className="text-[10px] font-bold text-foreground ml-auto">
                            {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(payload[1].value)}
                        </span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

export default function CompactFinancialChart({ data, title = "Pemasukan vs Pengeluaran", subtitle = "12 bulan terakhir" }: CompactFinancialChartProps) {
    return (
        <div className="bg-surface border border-border-main p-6 rounded-4xl shadow-sm flex flex-col h-full">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-sm font-bold text-foreground">{title}</h3>
                    <p className="text-[10px] text-secondary font-medium mt-0.5">{subtitle}</p>
                </div>
                <div className="flex gap-3">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span className="text-[9px] font-bold text-secondary uppercase tracking-wider">Masuk</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                        <span className="text-[10px] sm:text-[9px] font-bold text-secondary uppercase tracking-wider">Keluar</span>
                    </div>
                </div>
            </div>

            <div className="w-full grow min-h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{
                            top: 5,
                            right: 0,
                            left: 0,
                            bottom: 0,
                        }}
                        barGap={4}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2ECE6" opacity={0.4} />
                        <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#8da495', fontSize: 9, fontWeight: 600 }}
                            dy={5}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: 'var(--color-primary-soft)', opacity: 0.2, radius: 4 }}
                        />
                        <Bar
                            dataKey="income"
                            fill="var(--color-primary)"
                            radius={[3, 3, 3, 3]}
                            barSize={10}
                        />
                        <Bar
                            dataKey="expense"
                            fill="#cbd5e1"
                            radius={[3, 3, 3, 3]}
                            barSize={10}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
